import React from 'react';

export type FocusedSidebarLayoutProps<T extends { id: string }> = {
    streams: T[];
    /** Must match an entry in `streams` when non-empty. */
    focusedId: string;
    onFocusChange: (streamId: string) => void;
    renderTile: (args: { stream: T; isMainStage: boolean }) => React.ReactNode;
    onMainDoubleClick?: (streamId: string) => void;
    onThumbDoubleClick?: (streamId: string) => void;
    className?: string;
};

/**
 * Meet-style layout: large main stage + thumbnails (horizontal on small screens, vertical sidebar on md+).
 * Ported from server-master {@link FocusedSidebarLayout}.
 */
export function FocusedSidebarLayout<T extends { id: string }>({
    streams,
    focusedId,
    onFocusChange,
    renderTile,
    onMainDoubleClick,
    onThumbDoubleClick,
    className = '',
}: FocusedSidebarLayoutProps<T>) {
    const main = streams.find((s) => s.id === focusedId) ?? streams[0];
    const thumbs = main ? streams.filter((s) => s.id !== main.id) : [];

    if (!main) {
        return null;
    }

    return (
        <div
            className={`flex min-h-0 min-w-0 flex-1 flex-col gap-2 md:flex-row md:gap-3 ${className}`}
        >
            <div
                className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-black"
                onDoubleClick={() => onMainDoubleClick?.(main.id)}
            >
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    {renderTile({ stream: main, isMainStage: true })}
                </div>
            </div>

            {thumbs.length > 0 ? (
                <div
                    className="flex w-full shrink-0 flex-row justify-center gap-2 overflow-x-auto overflow-y-hidden py-1 [-webkit-overflow-scrolling:touch] md:w-[min(220px,32vw)] md:flex-col md:justify-center md:overflow-y-auto md:overflow-x-hidden md:py-0"
                    role="list"
                    aria-label="Other participants"
                >
                    {thumbs.map((stream) => (
                        /* `div` not `button`: tiles contain controls (`<button>`) — nested buttons break layout/clicks. */
                        <div
                            key={stream.id}
                            role="button"
                            tabIndex={0}
                            className="w-[min(160px,40vw)] shrink-0 cursor-pointer overflow-hidden rounded-xl border border-transparent text-left transition hover:border-sky-400/50 hover:shadow-md focus-visible:outline focus-visible:ring-2 focus-visible:ring-sky-400 md:aspect-video md:w-full md:max-h-[min(200px,28vh)]"
                            onClick={() => onFocusChange(stream.id)}
                            onDoubleClick={() => onThumbDoubleClick?.(stream.id)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onFocusChange(stream.id);
                                }
                            }}
                        >
                            {renderTile({ stream, isMainStage: false })}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
